import { Address, IAddress } from '@app/models/Address'
import { List, Record } from 'immutable'
import {
  AssociatedDevelopmentClient,
  IAssociatedDevelopmentClient,
} from './AssociatedDevelopmentClient'
import { Attachment, IAttachment } from './Attachment'
import {
  DevelopmentTeamRole,
  IDevelopmentTeamRole,
} from './DevelopmentTeamRole'
import { DevelopmentSiteStatus } from './enums'
import { IShareContact, ShareContact } from './ShareContact'

export interface IDevelopmentSite {
  readonly address: IAddress
  readonly attachments: List<IAttachment>
  readonly client: IAssociatedDevelopmentClient
  readonly constructionPrice: number
  readonly coverPhoto?: IAttachment
  readonly coverPhotoId: string
  readonly id: string
  readonly name: string
  readonly photos: List<IAttachment>
  readonly propertyId?: string
  readonly salesPrice: number
  readonly shareContacts: List<IShareContact>
  readonly status: DevelopmentSiteStatus
  readonly slug: string
  readonly team: List<IDevelopmentTeamRole>
  readonly title: string
}

const DevelopmentSiteRecord = Record({
  address: undefined,
  attachments: List<IAttachment>(),
  client: undefined,
  constructionPrice: 0,
  coverPhoto: undefined,
  coverPhotoId: '',
  id: '',
  name: '',
  photos: List<Attachment>(),
  propertyId: undefined,
  salesPrice: 0,
  shareContacts: List<ShareContact>(),
  slug: '',
  status: DevelopmentSiteStatus.Potential,
  team: List<DevelopmentTeamRole>(),
  title: '',
})

export class DevelopmentSite
  extends DevelopmentSiteRecord
  implements IDevelopmentSite {
  public static fromJS(initialState?: Partial<IDevelopmentSite>) {
    if (!initialState) {
      return new DevelopmentSite()
    }

    const {
      address,
      attachments,
      client,
      coverPhoto,
      photos,
      shareContacts,
      team,
      ...rest
    } = initialState

    return new DevelopmentSite({
      address: Address.fromJS(address),
      attachments: attachments
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : undefined,
      client: AssociatedDevelopmentClient.fromJS(client),
      coverPhoto: coverPhoto ? Attachment.fromJS(coverPhoto) : undefined,
      photos: photos
        ? List(photos.map((a) => Attachment.fromJS(a)))
        : undefined,
      shareContacts: shareContacts
        ? List(shareContacts.map((a) => ShareContact.fromJS(a)))
        : undefined,
      team: team
        ? List(team.map((a) => DevelopmentTeamRole.fromJS(a)))
        : undefined,
      ...rest,
    })
  }

  public readonly address: Address
  public readonly attachments: List<Attachment>
  public readonly client: AssociatedDevelopmentClient
  public readonly constructionPrice: number
  public readonly coverPhoto?: Attachment
  public readonly coverPhotoId: string
  public readonly id: string
  public readonly name: string
  public readonly photos: List<Attachment>
  public readonly propertyId?: string
  public readonly salesPrice: number
  public readonly shareContacts: List<ShareContact>
  public readonly slug: string
  public readonly status: DevelopmentSiteStatus
  public readonly team: List<DevelopmentTeamRole>
  public readonly title: string

  constructor(initialState?: Partial<DevelopmentSite>) {
    initialState ? super(initialState) : super()
  }
}
